import jwt_decode from "jwt-decode";
import { useEffect, useMemo, useRef } from "react";

import { LoginResponseBodyType } from "@intelligems/ig-types";
import { api } from "@src/api/api";
import { useFastOrgSwitch } from "@src/api/organizations/switch-organization";
import { useUser } from "@src/api/user-auths/login";
import { GENERAL_ROUTES } from "@src/routes/routes";
import { JwtToken } from "@src/types/user";
import { IgStorage } from "@src/utils/igStorage";
import { useMutation } from "@tanstack/react-query";

const appLaunchedCallback = async () => {
  const urlParams = new URLSearchParams(window.location.search);

  const params = {
    skipRedirect: "true",
  };
  for (const value of urlParams.keys()) {
    params[value] = urlParams.get(value);
  }

  const resp = await api.get<LoginResponseBodyType>(`/v2/app_launched`, {
    params,
  });
  return resp.data;
};

export const useAppLaunchedCallback = () => {
  return useMutation({
    mutationFn: async () => appLaunchedCallback(),
    onSuccess: (data) => {
      if (data.type === "redirect") {
        // eslint-disable-next-line react-compiler/react-compiler
        window.location.href = data.redirect;
      } else if (data.type === "clear") {
        IgStorage.clearAuthTokens();
        window.location.reload();
      } else {
        window.location.href = "/login";
      }
    },
  });
};

export const useAppLaunch = () => {
  const timeout = useRef<NodeJS.Timeout | null>(null);
  const searchParams = useMemo(
    () => new URLSearchParams(window.location.search),
    [window?.location?.search]
  );

  const token = IgStorage.authToken;
  const user = useUser();

  const switchOrg = useFastOrgSwitch();
  const appLaunchedCallback = useAppLaunchedCallback();

  const removeTimeout = () => {
    if (timeout.current) {
      clearTimeout(timeout.current);
    }
  };

  useEffect(() => {
    if (!token) {
      appLaunchedCallback.mutate(undefined, {
        onSuccess: () => {
          removeTimeout();
        },
      });
      return;
    }

    const shopParam = searchParams.get("shop");
    if (shopParam) {
      const decodedToken = jwt_decode<JwtToken>(token);
      const isMatchingShop = decodedToken.shopId === shopParam;
      if (!isMatchingShop) {
        switchOrg.mutate(
          {
            token,
            shopId: shopParam,
          },
          {
            onSuccess: () => {
              removeTimeout();
            },
          }
        );
        return;
      }
    }
  }, [token, searchParams]);

  useEffect(() => {
    if (
      user.isSuccess &&
      user.data.type === "login" &&
      user.data.organization.installed
    ) {
      window.location.pathname = GENERAL_ROUTES.Index;
    }
  }, [user.isSuccess]);

  useEffect(() => {
    timeout.current = setTimeout(() => {
      appLaunchedCallback.mutate(undefined, {
        onSuccess: () => {
          removeTimeout();
        },
      });
    }, 31000);
    return () => {
      removeTimeout();
    };
  }, []);
};
